import React, { useContext, useEffect, useState } from 'react';

import {
	DatabaseOutlined,
	DeleteOutlined,
	EllipsisOutlined,
	EyeOutlined,
	RestOutlined,
	ShareAltOutlined,
	StarFilled,
	StarOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Space, Tooltip, message, notification } from 'antd';

import {
	useAddRemoveFromFavorites,
	useArchivePost,
	useShareAnalysis,
} from '@/api/batch_process';
import { useDeleteVideoFromCollection } from '@/api/monitor_collections';
import getCanUserArchiveCopilotPosts from '@/lib/helpers/getCanUserArchiveCopilotPosts';
import ShareLinkNotification from '@/pages/app/dashboard/video-insights-co-pilot/ShareLinkNotification';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { PageContext } from '@/providers/PageContextProvider';

type TProps = {
	record: any;
	keys: string[];
};

interface IResponseError {
	response: {
		data: {
			message: string;
			error: string;
		};
	};
	message: string;
}

export default function MultiButton({ record, keys }: TProps) {
	// const { setIsDrawerOpen, setHistoryItem, historyItemId } = useHistoryStore();
	const { drawer, closeDrawer, openDrawer } = useContext(PageContext);
	const { setIsAddToCollectionModalOpen, setModalData } =
		useContext(CollectionContext);
	const [api, contextHolder] = notification.useNotification({ maxCount: 1 });
	const [messageApi] = message.useMessage();

	const {
		mutate: shareVideo,
		isLoading: isShareLoading,
		isSuccess: isShareSuccess,
		data,
	} = useShareAnalysis();

	const { mutate: deleteVideoItem, isLoading: isDeleteLoading } =
		useDeleteVideoFromCollection(record?.user_video_collection_id);

	const { mutate: addToFavourites, isLoading: isAddToFavouritesLoading } =
		useAddRemoveFromFavorites();

	const { mutate: archivePost, isLoading: isArchiveLoading } = useArchivePost();

	const openNotification = (pauseOnHover: boolean) =>
		api.success({
			message: 'Report link generated',
			description:
				"Here's the report you can share with your colleagues and clients!",
			duration: 10,
			showProgress: true,
			pauseOnHover,
			btn: <ShareLinkNotification data={data} />,
		});

	useEffect(() => {
		if (isShareSuccess) {
			openNotification(true);
			messageApi.destroy();
		}
	}, [isShareSuccess]);

	const onShareClick = async () => {
		messageApi.open({
			type: 'loading',
			content: 'Preparing share link..',
			duration: 0,
		});

		shareVideo(
			{
				userId: Number(localStorage?.getItem('sv_extension_uid')),
				url: record?.url,
			},
			{
				// onSuccess: () => {
				//   openNotification(true);
				//   messageApi.destroy();
				// },
				onError: (error: any) => {
					const shareError = error as IResponseError;
					messageApi.destroy();

					if (shareError?.response?.data?.error) {
						message.error(
							`Something went wrong. Please try again: ${shareError?.response?.data?.error}`,
						);
					} else {
						message.error(
							`We have encountered an error on sharing this video. Please try again.`,
						);
					}
				},
			},
		);
	};

	const onAddToFavouritesClick = () => {
		addToFavourites(
			{
				id:
					record?.copilot_post_id ?? record?.id ?? drawer?.data?.historyItemId,
				isAdd: !record?.favourite,
			},
			{
				// onSuccess: () => {
				// 	message.success('Updated');
				// },
				onError: (error: any) => {
					const err = error as IResponseError;
					message.error(
						`Something went wrong. Please try again: ${
							err?.response?.data?.message || err?.message
						}`,
					);
				},
			},
		);
	};

	const onDeleteVideoItemClick = () => {
		deleteVideoItem(
			{
				collection_id: record?.user_video_collection_id,
				post_id: record?.copilot_post_id ?? record?.id,
			},
			{
				onSuccess: () => {
					message.success('Removed');
				},
				onError: (error: any) => {
					const err = error as IResponseError;
					message.error(
						`Something went wrong. Please try again: ${
							err?.response?.data?.message || err?.message
						}`,
					);
				},
			},
		);
	};

	const onArchiveClick = () => {
		archivePost(record?.id, {
			onSuccess: () => {
				message.success('Archived');
			},
			onError: (error: any) => {
				const err = error as IResponseError;
				message.error(
					`Something went wrong. Please try again: ${
						err?.response?.data?.message || err?.message
					}`,
				);
			},
		});
	};

	const handleMenuClick: MenuProps['onClick'] = (e) => {
		// console.log("click", e);

		switch (e.key) {
			case 'view':
				openDrawer('view-copilot-video-details', {
					url: record?.url,
					id: record?.id,
				});
				break;
			case 'share':
				onShareClick();
				break;
			case 'favourite':
				onAddToFavouritesClick && onAddToFavouritesClick();
				break;
			case 'collection':
				// message.info("Coming soon... ☺️");
				setModalData(
					!record?.id ? { ...record, id: drawer?.data?.historyItemId } : record,
				);
				// setIsDrawerOpen(false);
				setIsAddToCollectionModalOpen(true);
				// onAddToCollectionClick && onAddToCollectionClick();
				break;
			case 'archive':
				// message.info("Coming soon... ☺️");
				onArchiveClick();
				// onDeleteVideoItemClick();
				// onAddToCollectionClick && onAddToCollectionClick();
				break;
			case 'delete':
				onDeleteVideoItemClick();

				// message.info("Coming soon... ☺️");
				break;
			default:
				break;
		}
	};

	const items: MenuProps['items'] = [
		{
			label: 'View report',
			key: 'view',
			icon: <EyeOutlined style={{ fontSize: '14px' }} />,
			disabled: record?.status !== 2,
		},
		{
			label: 'Share',
			key: 'share',
			icon: <ShareAltOutlined style={{ fontSize: '14px' }} />,
			disabled: record?.status !== 2 || isShareLoading,
		},
		{
			label: record?.favourite ? 'Remove from favourites' : 'Add to favorites',
			key: 'favourite',
			icon: record?.favourite ? (
				<StarFilled style={{ fontSize: '14px' }} />
			) : (
				<StarOutlined style={{ fontSize: '14px' }} />
			),
			disabled: record?.status !== 2 || isAddToFavouritesLoading,
		},
		{
			label: 'Add to collection',
			key: 'collection',
			icon: <DatabaseOutlined style={{ fontSize: '14px' }} />,
			disabled: record?.status !== 2,
		},
		{
			label: 'Remove from collection',
			key: 'delete',
			icon: <DeleteOutlined style={{ fontSize: '14px' }} />,
			disabled: record?.status !== 2 || isDeleteLoading,
		},
	];

	const menuProps = {
		items: items?.filter((item) => keys?.includes(item?.key as string)),
		onClick: handleMenuClick,
	};

	if (
		record?.status > 2 &&
		keys?.includes('archive') &&
		getCanUserArchiveCopilotPosts()
	) {
		menuProps.items.push({
			label: 'Archive',
			key: 'archive',
			icon: <RestOutlined style={{ fontSize: '14px' }} />,
			// disabled: record?.status === 2,
			danger: true,
		});
	}

	return (
		<>
			{contextHolder}
			<Dropdown menu={menuProps} placement="bottomRight">
				<Button
					size="middle"
					// size="small"
					// disabled={
					//   isAddToFavouritesLoading ||
					//   isShareLoading ||
					//   isDeleteLoading ||
					//   isArchiveLoading
					// }
				>
					{isAddToFavouritesLoading ||
					isShareLoading ||
					isDeleteLoading ||
					isArchiveLoading ? (
						<SyncOutlined spin />
					) : (
						<EllipsisOutlined />
					)}
				</Button>
			</Dropdown>
		</>
	);
}
